
import { computed, defineComponent, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  Invoice,
  InvoiceCollectionStatus,
} from "@/helpers/invoice/typesDefinition";
import { formatPrice } from "@/helpers/utils";
import dayjs from "dayjs";
import { User } from "@/helpers/user/typesDefinition";
import { fullName } from "@/helpers/user/utils";
import { isOrder } from "@/helpers/order/utils";
import { GET_OPENED_INVOICES } from "@/graphql/invoice/queries";
import BasicButton from "@/components/BasicButton.vue";
import EditInvoice from "@/components/CRUXEntity/EditInvoice.vue";
import PaginatedEntitiesList from "@/components/list/PaginatedEntitiesList.vue";
import { DocumentNode } from "graphql";
import { useMutation } from "@vue/apollo-composable";
import { UPDATE_ASSIGNED_TO } from "@/graphql/invoice/mutations";
import config from "@/config";
import { useToast } from "vue-toastification";

export default defineComponent({
  props: {
    hasCheckBox: {
      type: Boolean,
      default: false,
    },
    avatarColumn: {
      type: String,
      required: false,
    },
    query: {
      type: Object as PropType<DocumentNode>,
      required: true,
    },
    queryParams: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    refetchQueries: {
      type: Array as PropType<{ query: DocumentNode; variables?: object }[]>,
      required: false,
      default: () => [],
    },
  },
  setup(props) {
    const { t } = useI18n({
      useScope: "global",
      inheritLocale: true,
    });

    const toast = useToast();

    const loading = ref(false);
    const checkBoxes = ref([] as boolean[]);
    const selectAll = ref(false);
    const clickedInvoice = ref();
    const openedInvoices = ref<Invoice[]>([]);

    const {
      mutate: updateAssignedTo,
      onDone: onDoneUpdateAssignedTo,
      onError: onErrorUpdateAssignedTo,
    } = useMutation(UPDATE_ASSIGNED_TO, {
      refetchQueries: props.refetchQueries,
    });
    onDoneUpdateAssignedTo(() => {
      for (const index in checkBoxes.value) {
        checkBoxes.value[index] = false;
      }
      toast.success(t("success.assignedTo"));
    });
    onErrorUpdateAssignedTo((error) => {
      console.error(error);
      toast.error(t("errors.assignedTo"));
    });

    async function sendInvoicesToFinancialOfficer(
      invoiceIds: string[],
    ): Promise<void> {
      if (!invoiceIds.length) {
        toast.warning(t("warning.noSelection"));
        return;
      }
      await updateAssignedTo({
        invoiceIds,
        assignedTo: config.financialOfficerId,
      });
      toast.warning("Financial officer dashboard not implemented yet");
    }

    async function sendSelectedInvoicesToFinancialOfficer(
      openedInvoices: Invoice[],
      checkBoxes: boolean[],
      selectAll: boolean,
    ): Promise<void> {
      const invoicedIds = openedInvoices.map((invoice: Invoice) => invoice.id);
      if (checkBoxes) {
        const selectedInvoicedIds = invoicedIds.filter(
          (_id: string, index: number) => checkBoxes[index],
        );
        return sendInvoicesToFinancialOfficer(selectedInvoicedIds);
      } else if (selectAll) {
        return sendInvoicesToFinancialOfficer(invoicedIds);
      } // Else nothing was selected
      toast.warning(t("warning.noSelection"));
    }

    const kpis = computed(() => {
      const totalInvoiced = openedInvoices.value
        .map((invoice: Invoice) => invoice.total)
        .reduce((acc, val) => acc + val, 0);
      const totalPaid = openedInvoices.value
        .map((invoice: Invoice) => invoice.total - invoice.balance)
        .reduce((acc, val) => acc + val, 0);
      const totalDue = openedInvoices.value
        .map((invoice: Invoice) => invoice.balance)
        .reduce((acc, val) => acc + val, 0);

      return {
        totalInvoiced: {
          label: "totalInvoiced", //t("project.budget.label.total"),
          value: formatPrice(totalInvoiced),
        },
        totalPaid: {
          label: "totalPaid", //t("project.budget.label.invoiced"),
          value: formatPrice(totalPaid),
        },
        totalDue: {
          label: "totalDue", //t("project.budget.label.performed"),
          value: formatPrice(totalDue),
        },
      };
    });

    return {
      t,
      loading,
      checkBoxes,
      selectAll,
      openedInvoices,
      clickedInvoice,
      kpis,
      sendSelectedInvoicesToFinancialOfficer,
    };
  },
  data() {
    return {
      GET_OPENED_INVOICES,
      isEditModalVisible: false,
    };
  },
  components: {
    BasicButton,
    EditInvoice,
    PaginatedEntitiesList,
  },
  watch: {
    itemsRef(newValues) {
      this.$emit("update:items", newValues);
    },
  },
  methods: {
    showEditModal(invoice: Invoice): void {
      this.clickedInvoice = invoice;
      this.isEditModalVisible = true;
    },
    closeEditModal(): void {
      this.isEditModalVisible = false;
    },
    formatClient(
      _value: undefined,
      _name: string,
      invoice: Invoice,
    ): string | undefined {
      return invoice.company.client.name;
    },
    formatPaid(_value: undefined, _name: string, invoice: Invoice) {
      return formatPrice(invoice.total - invoice.balance);
    },
    formatDate(value: string | undefined) {
      if (!value) return value;
      return dayjs(value).format("DD/MM/YYYY");
    },
    reminderCount(_value: undefined, _name: string, invoice: Invoice) {
      const match = invoice.invoiceStatus.match(/^reminder_(\d+)$/);
      if (match && match.length > 0) {
        return match[1];
      }
      return "-";
    },
    formatAssignedTo(user: User): { name: string; avatarUrl?: string } {
      return {
        name: fullName(user),
        avatarUrl: user?.avatarUrl,
      };
    },
    formatPaymentOption(
      _value: undefined,
      _name: string,
      invoice: Invoice,
    ): string | undefined {
      return invoice.linkedResource && isOrder(invoice.linkedResource)
        ? this.t("paymentOptionLabels." + invoice.linkedResource.paymentOption)
        : undefined;
    },
    formatCollectionStatus(
      collectionStatus: InvoiceCollectionStatus,
    ): string | undefined {
      return this.t("collectionStatus." + collectionStatus);
    },
  },
});
